'use client'

import { DependencyList, RefObject, useEffect } from 'react'

/**
 * Subscribes to native `change` and `reset` events on a select element and its form.
 * Calls the provided callbacks when those events fire; useful for keeping React state
 * in sync with native form behavior (e.g. when the form is reset via a reset button).
 *
 * @param selectInputRef - Ref to the underlying `<select>` DOM element
 * @param callbacks - `onChange` when the select value changes; `onReset` when the form is reset
 * @param dependencies - Additional values that trigger effect re-run (listeners are re-attached when these change)
 */
export const useNativeSelectChangeReset = (
    selectInputRef: RefObject<HTMLSelectElement | null>,
    {
        onChange,
        onReset,
    }: {
        onChange: () => void
        onReset: () => void
    },
    dependencies: DependencyList,
): void => {
    useEffect(() => {
        const selectElement = selectInputRef.current
        if (!selectElement) return

        const abortController = new AbortController()
        const { signal } = abortController

        const handleChange = () => onChange()
        const handleReset = () => onReset()
        selectElement.addEventListener('change', handleChange, { signal })
        selectElement.form?.addEventListener('reset', handleReset, { signal })

        return () => abortController.abort()
    }, [onChange, onReset, ...dependencies])
}
